/* Home: Brand strip. Fetches theme/assets/brands-directory.json (same data
   source as /pages/brands, see brands-directory.js's header comment for
   why this isn't rendered via shop.metaobjects.brand.values) and renders a
   logo grid/marquee + optional industry tabs. Hides the whole section if
   no brand qualifies (logo_only, the default) - never ships an empty or
   placeholder-text strip. Vanilla JS, no deps. */
(function () {
  'use strict';
  var root = document.querySelector('[data-home-brand-strip]');
  if (!root) return;

  var jsonUrl = root.getAttribute('data-brands-json');
  var logoOnly = root.getAttribute('data-logo-only') === 'true';
  var maxBrands = parseInt(root.getAttribute('data-max-brands'), 10) || 12;
  var layout = root.getAttribute('data-layout') || 'grid';
  var enableIndustryTabs = root.getAttribute('data-enable-industry-tabs') === 'true';
  var allTabLabel = root.getAttribute('data-all-tab-label') || 'All brands';
  var tabsEl = root.querySelector('[data-home-brand-strip-tabs]');
  var contentEl = root.querySelector('[data-home-brand-strip-content]');

  if (!jsonUrl) return;

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function logoHtml(brand, hidden, eager) {
    var name = escapeHtml(brand.name);
    var href = escapeHtml(brand.collection_url || '');
    /* every tile a buyer can see must navigate - the aria-hidden marquee clones fill
       the viewport for the last part of each loop, so they are links too, just
       removed from the tab order (tabindex=-1) so they add no tab stops */
    var open = !href
      ? '<div class="home-brand-strip__logo"' + (hidden ? ' aria-hidden="true"' : '') + '>'
      : '<a class="home-brand-strip__logo" href="' + href + '"' + (hidden ? ' aria-hidden="true" tabindex="-1"' : ' aria-label="' + name + '"') + '>';
    var close = !href ? '</div>' : '</a>';
    if (brand.logo_url) {
      /* tiles display at ~120 CSS px: serve 120w (1x) and 240w (2x), never the 400px file; always lazy -
         the strip sits below the fold and must not compete with the hero for bandwidth */
      var sep = brand.logo_url.indexOf('?') === -1 ? '?' : '&';
      var src1 = brand.logo_url + sep + 'width=120';
      var src2 = brand.logo_url + sep + 'width=240';
      return (
        open +
        '<img src="' + src1 + '" srcset="' + src1 + ' 120w, ' + src2 + ' 240w" sizes="120px" alt="' + (hidden ? '' : name) + '" loading="lazy" decoding="async" width="120" height="54">' +
        close
      );
    }
    return open + '<span class="home-brand-strip__logo-text" title="' + name + '">' + name + '</span>' + close;
  }

  function render(data) {
    var allBrands = data.brands || [];
    var industries = data.industries || [];
    var brands = logoOnly ? allBrands.filter(function (b) { return b.logo_url; }) : allBrands;

    if (brands.length === 0) {
      root.style.display = 'none'; // hide-when-empty (the root ships with a min-height placeholder so it can be observed)
      return;
    }

    var showTabs = false;
    if (enableIndustryTabs && industries.length > 0) {
      showTabs = brands.some(function (b) {
        return (b.industries || []).length > 0;
      });
    }

    if (showTabs) {
      var tabsHtml =
        '<div class="home-brand-strip__tabs" role="tablist" data-brand-strip-tabs>' +
        '<button type="button" class="home-brand-strip__tab is-active" role="tab" aria-selected="true" data-brand-tab="all">' +
        escapeHtml(allTabLabel) + '</button>';
      industries.forEach(function (industry) {
        var hasBrand = brands.some(function (b) { return (b.industries || []).indexOf(industry.handle) !== -1; });
        if (!hasBrand) return;
        tabsHtml +=
          '<button type="button" class="home-brand-strip__tab" role="tab" aria-selected="false" data-brand-tab="' +
          escapeHtml(industry.handle) + '">' + escapeHtml(industry.name) + '</button>';
      });
      tabsHtml += '</div>';
      tabsEl.innerHTML = tabsHtml;
    }

    /* order: product_count (recognisable, deep-catalog brands first - default),
       alpha (directory order), featured (brand.featured first, then count) */
    var order = root.getAttribute('data-order') || 'product_count';
    if (order === 'product_count' || order === 'featured') {
      brands = brands.slice().sort(function (a, b) {
        if (order === 'featured' && !!a.featured !== !!b.featured) return a.featured ? -1 : 1;
        return (b.product_count || 0) - (a.product_count || 0) || String(a.name).localeCompare(String(b.name));
      });
    }
    var limited = brands.slice(0, maxBrands);

    function itemAttrs(b) {
      return (showTabs ? ' data-brand-industries="' + escapeHtml((b.industries || []).join(' ')) + '"' : '');
    }

    var html;
    if (layout === 'grid') {
      html =
        '<ul class="home-brand-strip__grid list-unstyled"' + (showTabs ? ' data-brand-strip-list' : '') + '>' +
        limited
          .map(function (b) {
            return '<li class="home-brand-strip__logo-item"' + itemAttrs(b) + '>' + logoHtml(b, false) + '</li>';
          })
          .join('') +
        '</ul>';
    } else {
      /* marquee: every tile is about to scroll into view, so load eagerly */
      var track = limited.map(function (b) { return logoHtml(b, false, true); }).join('');
      var trackRepeat = limited.map(function (b) { return logoHtml(b, true, true); }).join('');
      var pauseLabel = escapeHtml(root.getAttribute('data-pause-label') || 'Pause');
      var playLabel = escapeHtml(root.getAttribute('data-play-label') || 'Play');
      html =
        '<div class="home-scroller home-scroller--marquee"><div class="home-scroller__track">' +
        track + trackRepeat + '</div></div>';
      /* WCAG 2.2.2: auto-moving content needs a user pause control - it lives in
         the header row next to "View all brands", not floating over the scroller */
      var header = root.querySelector('.home-brand-strip__header');
      if (header && !header.querySelector('[data-marquee-toggle]')) {
        var viewAll = header.querySelector('.home-brand-strip__view-all');
        var actions = document.createElement('div');
        actions.className = 'home-brand-strip__actions';
        actions.innerHTML = '<button type="button" class="home-brand-strip__pause" data-marquee-toggle aria-pressed="false" data-pause-label="' + pauseLabel + '" data-play-label="' + playLabel + '">' + pauseLabel + '</button>';
        if (viewAll) actions.appendChild(viewAll);
        header.appendChild(actions);
      }
    }
    contentEl.innerHTML = html;
    root.style.display = '';
    root.style.minHeight = '';
    /* constant speed in px/s regardless of how many logos: duration = half track / speed */
    var toggle = root.querySelector('[data-marquee-toggle]');
    if (toggle) {
      toggle.addEventListener('click', function () {
        var paused = root.classList.toggle('is-paused');
        toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
        toggle.textContent = paused ? toggle.getAttribute('data-play-label') : toggle.getAttribute('data-pause-label');
      });
    }
    /* Keyboard: the track moves with a CSS transform, which scrollIntoView
       cannot undo, so an early tile could sit fully off-screen when focused.
       While focus is inside the scroller the track is parked at translateX(0)
       (is-keyboard) and the tile is scrolled into view; leaving resumes. */
    /* a mouse/touch press also fires focusin (on mousedown): parking the track then
       moves the tile out from under the pointer before mouseup, so the first click
       never lands (user-reported). Only keyboard focus (:focus-visible) parks it. */
    var pointerDown = false;
    contentEl.addEventListener('pointerdown', function () { pointerDown = true; }, true);
    /* the release can land outside the strip (drag out, release off-window, alt-tab) - reset globally */
    window.addEventListener('pointerup', function () { pointerDown = false; }, true);
    window.addEventListener('pointercancel', function () { pointerDown = false; }, true);
    window.addEventListener('blur', function () { pointerDown = false; });
    contentEl.addEventListener('focusin', function (e) {
      var tile = e.target.closest('.home-brand-strip__logo');
      if (!tile || pointerDown) return;
      var byKeyboard = true; /* no :focus-visible support: the pointerDown guard above still prevents the click bug */
      try { byKeyboard = tile.matches(':focus-visible'); } catch (err) {}
      if (!byKeyboard) return;
      root.classList.add('is-keyboard');
      if (tile.scrollIntoView) tile.scrollIntoView({ inline: 'center', block: 'nearest' });
    });
    contentEl.addEventListener('focusout', function (e) {
      if (!e.relatedTarget || !contentEl.contains(e.relatedTarget)) root.classList.remove('is-keyboard');
    });
    var trackEl = contentEl.querySelector('.home-scroller__track');
    var pxPerSec = parseFloat(root.getAttribute('data-speed')) || 35;
    if (trackEl) {
      var setDuration = function () {
        var half = trackEl.scrollWidth / 2;
        if (half > 0) root.style.setProperty('--home-marquee-duration', (half / pxPerSec).toFixed(1) + 's');
      };
      setDuration();
      window.addEventListener('resize', setDuration, { passive: true });
    }

    if (showTabs) {
      setupTabs();
    }
  }

  function setupTabs() {
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-brand-tab]'));
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-brand-industries]'));
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        tabs.forEach(function (t) {
          t.classList.toggle('is-active', t === tab);
          t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
        });
        var industry = tab.getAttribute('data-brand-tab');
        items.forEach(function (item) {
          if (industry === 'all') {
            item.hidden = false;
          } else {
            var list = (item.getAttribute('data-brand-industries') || '').split(' ');
            item.hidden = list.indexOf(industry) === -1;
          }
        });
      });
    });
  }

  /* fetch the brand file and render only when the strip is within 600px of the viewport */
  function load() {
    fetch(jsonUrl)
      .then(function (res) { return res.json(); })
      .then(render)
      .catch(function () {
        root.style.display = 'none'; /* fetch failed: never show an empty strip */
      });
  }
  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      if (entries.some(function (e) { return e.isIntersecting; })) { io.disconnect(); load(); }
    }, { rootMargin: '600px 0px' });
    io.observe(root);
  } else {
    load();
  }
})();
